// On-screen piano + computer-keyboard input. Emits MIDI note numbers.
// Exposes a small controller so the frequency ribbon can mirror its pitch on
// the keyboard: it highlights the nearest key, follows the octave so that key
// stays on screen, and leaves a faint "ghost" of the last note until the next
// input.

const WHITE_SEMITONES = [0, 2, 4, 5, 7, 9, 11];
const BLACK_AFTER = new Set([0, 2, 5, 7, 9]); // semitone offsets that have a black key to their right

// Home-row keyboard mapping for one octave, relative to the visible base note.
const KEY_TO_OFFSET: Record<string, number> = {
  a: 0, w: 1, s: 2, e: 3, d: 4, f: 5, t: 6, g: 7, y: 8, h: 9, u: 10, j: 11, k: 12,
};

// Don't grab keystrokes while the user is typing in a field.
function isTyping(): boolean {
  const el = document.activeElement;
  return el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement;
}

const mod12 = (n: number): number => ((n % 12) + 12) % 12;

export interface KeyboardOptions {
  startMidi: number;
  octaves: number;
  onNoteOn: (midi: number) => void;
  onNoteOff: (midi: number) => void;
}

export interface KeyboardController {
  /** Mirror the frequency ribbon: highlight the nearest key, shifting the
   *  visible octave to keep it on screen. Pass null when the ribbon is released
   *  (the note stays as a ghost until the next input). */
  setExternal(midi: number | null): void;
}

export function buildKeyboard(container: HTMLElement, opts: KeyboardOptions): KeyboardController {
  container.classList.add("keyboard");
  const octaves = opts.octaves;
  const span = octaves * 12;
  const keyEls = new Map<number, HTMLElement>();
  const sounding = new Set<number>();
  let viewStart = opts.startMidi;
  let externalMidi: number | null = null;
  let ghostMidi: number | null = null;

  function applyClasses(): void {
    for (const [midi, el] of keyEls) {
      const active = sounding.has(midi) || midi === externalMidi;
      el.classList.toggle("active", active);
      el.classList.toggle("ghost", !active && midi === ghostMidi);
    }
  }

  function bindPointer(el: HTMLElement, midi: number): void {
    el.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      press(midi);
    });
    el.addEventListener("pointerenter", (e) => {
      if (e.buttons === 1) press(midi);
    });
    el.addEventListener("pointerleave", () => release(midi));
  }

  function render(): void {
    container.innerHTML = "";
    keyEls.clear();
    const whiteKeys: number[] = [];
    for (let i = 0; i <= span; i++) {
      const midi = viewStart + i;
      if (WHITE_SEMITONES.includes(mod12(midi))) whiteKeys.push(midi);
    }
    const w = 100 / whiteKeys.length;
    whiteKeys.forEach((midi, idx) => {
      const el = document.createElement("div");
      el.className = "key white";
      el.style.left = `${idx * w}%`;
      el.style.width = `${w}%`;
      bindPointer(el, midi);
      keyEls.set(midi, el);
      container.append(el);
    });
    whiteKeys.forEach((midi, idx) => {
      if (!BLACK_AFTER.has(mod12(midi)) || idx === whiteKeys.length - 1) return;
      const blackMidi = midi + 1;
      const el = document.createElement("div");
      el.className = "key black";
      el.style.left = `${(idx + 1) * w - w * 0.3}%`;
      el.style.width = `${w * 0.6}%`;
      bindPointer(el, blackMidi);
      keyEls.set(blackMidi, el);
      container.append(el);
    });
    applyClasses();
  }

  function ensureVisible(midi: number): void {
    if (midi >= viewStart && midi <= viewStart + span) return;
    const vs = Math.max(12, Math.min(108 - span, Math.floor(midi / 12) * 12 - 12));
    if (vs !== viewStart) {
      viewStart = vs;
      render();
    }
  }

  function press(midi: number): void {
    if (sounding.has(midi)) return;
    sounding.add(midi);
    ghostMidi = midi;
    applyClasses();
    opts.onNoteOn(midi);
  }
  function release(midi: number): void {
    if (!sounding.has(midi)) return;
    sounding.delete(midi);
    ghostMidi = midi;
    applyClasses();
    opts.onNoteOff(midi);
  }
  function releaseAll(): void {
    for (const midi of [...sounding]) release(midi);
  }

  render();

  window.addEventListener("pointerup", releaseAll);
  window.addEventListener("keydown", (e) => {
    if (e.repeat || isTyping()) return;
    if (e.key === "z") {
      releaseAll();
      viewStart = Math.max(12, viewStart - 12);
      render();
      return;
    }
    if (e.key === "x") {
      releaseAll();
      viewStart = Math.min(108 - span, viewStart + 12);
      render();
      return;
    }
    const off = KEY_TO_OFFSET[e.key.toLowerCase()];
    if (off !== undefined) press(viewStart + off);
  });
  window.addEventListener("keyup", (e) => {
    if (isTyping()) return;
    const off = KEY_TO_OFFSET[e.key.toLowerCase()];
    if (off !== undefined) release(viewStart + off);
  });

  return {
    setExternal(midi: number | null): void {
      if (midi === null) {
        externalMidi = null;
        applyClasses();
        return;
      }
      const m = Math.round(midi);
      ensureVisible(m);
      externalMidi = m;
      ghostMidi = m;
      applyClasses();
    },
  };
}
